import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Shield, 
  Key, 
  Activity,
  TrendingUp,
  Server,
  Clock,
  CreditCard,
  Package
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      description: 'All registered users',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Administrators',
      value: stats?.total_admins || 0,
      description: 'Admin & moderator accounts',
      icon: Shield,
      color: 'bg-green-500'
    },
    {
      title: 'Members',
      value: stats?.total_members || 0,
      description: 'Regular member accounts',
      icon: Users,
      color: 'bg-yellow-500'
    },
    {
      title: 'Active Users',
      value: stats?.active_users || 0,
      description: 'Currently active accounts',
      icon: Activity,
      color: 'bg-emerald-500'
    },
    {
      title: 'API Keys',
      value: stats?.api_keys_configured || 0,
      description: 'Configured integrations',
      icon: Key,
      color: 'bg-purple-500'
    },
    {
      title: 'System Status',
      value: 'Online',
      description: 'All systems operational',
      icon: Server,
      color: 'bg-green-500'
    }
  ];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <div className={`w-8 h-8 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              System Overview
            </CardTitle>
            <CardDescription>
              Current system status and health metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Database Connection</span>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Connected
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">API Server</span>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Running
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Background Tasks</span>
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                Scheduled
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Storage</span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                Available
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common administrative tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <a 
              href="/admin/products" 
              className="block w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center">
                <Package className="h-4 w-4 mr-2 text-green-600" />
                <div className="font-medium text-sm">Products & Allocations</div>
              </div>
              <div className="text-xs text-gray-500 ml-6">Manage offerings by phase, level, and category</div>
            </a>
            <a 
              href="/admin/nowpayments" 
              className="block w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center">
                <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                <div className="font-medium text-sm">Configure NOWPayments</div>
              </div>
              <div className="text-xs text-gray-500 ml-6">Set up cryptocurrency payment processing</div>
            </a>
            <button className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <div className="font-medium text-sm">Create New User</div>
              <div className="text-xs text-gray-500">Add new administrator or member account</div>
            </button>
            <button className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <div className="font-medium text-sm">View Audit Logs</div>
              <div className="text-xs text-gray-500">Monitor recent system activities</div>
            </button>
          </CardContent>
        </Card>
      </div>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Last Updated:</span>
              <div className="text-gray-900">
                {stats?.last_updated ? new Date(stats.last_updated).toLocaleString() : 'N/A'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Platform Version:</span>
              <div className="text-gray-900">BBC Quick List v1.0.0</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Environment:</span>
              <div className="text-gray-900">Production</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminDashboard;